import { useEffect, useRef } from 'react';

interface UseScannerInputOptions {
    onScan: (code: string) => void;
    enabled?: boolean;
    // Minimum length of scanned code
    minLength?: number;
}

/**
 * Hook to detect barcode scanner input.
 * Barcode scanners act as keyboard devices - they type characters rapidly
 * and end with an Enter key. This hook collects all keystrokes and submits
 * when Enter is pressed. A timeout auto-clears the buffer if no Enter arrives.
 */
export const useScannerInput = ({
    onScan,
    enabled = true,
    minLength = 4,
}: UseScannerInputOptions) => {
    const bufferRef = useRef<string>('');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onScanRef = useRef(onScan);

    // Keep ref up to date without re-registering the listener
    useEffect(() => {
        onScanRef.current = onScan;
    }, [onScan]);

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // If the active element is an input/textarea that is NOT our scan page,
            // skip to avoid interfering with form inputs
            const activeEl = document.activeElement;
            if (
                activeEl &&
                (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') &&
                !activeEl.hasAttribute('data-scan-input')
            ) {
                return;
            }

            if (e.key === 'Enter') {
                e.preventDefault();
                if (bufferRef.current.length >= minLength) {
                    onScanRef.current(bufferRef.current.trim());
                }
                bufferRef.current = '';
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                return;
            }

            // Only accept printable characters
            if (e.key.length !== 1) return;

            bufferRef.current += e.key;

            // Auto-reset buffer after 2 seconds of no input (in case Enter is missed)
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                bufferRef.current = '';
            }, 2000);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [enabled, minLength]);

    return {};
};
